/**
 * What was dropped on the page: files to open, or a folder to open as the
 * workspace.
 *
 * The browser hands over a dropped file's handle only during the drop event
 * itself, so everything is gathered synchronously here and resolved after.
 * Where there are handles (File System Access), a dropped file can be saved
 * back where it came from; elsewhere it opens as a copy, and a dropped folder
 * is read as a snapshot.
 */
import { pathForFileHandle } from './handles'

export type Dropped =
  | { kind: 'path'; path: string }
  | { kind: 'file'; file: File }
  | { kind: 'folder'; handle: FileSystemDirectoryHandle }
  | { kind: 'snapshot'; name: string; files: Array<{ path: string; file: File }> }

type ItemWithHandle = DataTransferItem & {
  getAsFileSystemHandle?: () => Promise<FileSystemHandle | null>
}

/** Reads a directory entry's files, recursively, for browsers without handles. */
async function readEntries(
  dir: FileSystemDirectoryEntry,
  prefix = ''
): Promise<Array<{ path: string; file: File }>> {
  const out: Array<{ path: string; file: File }> = []
  const reader = dir.createReader()
  // readEntries returns at most 100 at a time; call until it returns none.
  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((resolve, reject) =>
      reader.readEntries(resolve, reject)
    )
    if (batch.length === 0) break
    for (const entry of batch) {
      const path = prefix ? `${prefix}/${entry.name}` : entry.name
      if (entry.isDirectory) {
        if (entry.name.startsWith('.') || entry.name === 'node_modules') continue
        out.push(...(await readEntries(entry as FileSystemDirectoryEntry, path)))
      } else {
        const file = await new Promise<File>((resolve, reject) =>
          (entry as FileSystemFileEntry).file(resolve, reject)
        )
        out.push({ path, file })
      }
    }
  }
  return out
}

/**
 * Starts reading a drop. Call it inside the drop event; it returns what was
 * dropped once it has all been read. `wanted` says which file names to open.
 */
export function readDrop(dt: DataTransfer, wanted: (name: string) => boolean): Promise<Dropped[]> {
  const jobs: Array<Promise<Dropped | null>> = []
  for (const item of [...dt.items]) {
    if (item.kind !== 'file') continue
    const withHandle = item as ItemWithHandle
    const entry = item.webkitGetAsEntry?.() ?? null
    if (typeof withHandle.getAsFileSystemHandle === 'function') {
      const file = item.getAsFile()
      jobs.push(
        withHandle.getAsFileSystemHandle().then(async (handle) => {
          if (handle?.kind === 'directory')
            return { kind: 'folder', handle: handle as FileSystemDirectoryHandle }
          if (handle?.kind === 'file' && wanted(handle.name)) {
            return { kind: 'path', path: await pathForFileHandle(handle as FileSystemFileHandle) }
          }
          return file && wanted(file.name) && !handle ? { kind: 'file', file } : null
        })
      )
    } else if (entry?.isDirectory) {
      jobs.push(
        readEntries(entry as FileSystemDirectoryEntry).then((files) => ({
          kind: 'snapshot',
          name: entry.name,
          files,
        }))
      )
    } else {
      const file = item.getAsFile()
      if (file && wanted(file.name)) jobs.push(Promise.resolve({ kind: 'file', file }))
    }
  }
  return Promise.all(jobs).then((all) => all.filter((d): d is Dropped => d !== null))
}

/** Whether a drop holds anything this page would open: checked during dragover and drop. */
export function dropHasOpenable(dt: DataTransfer, wanted: (name: string) => boolean): boolean {
  for (const item of [...dt.items]) {
    if (item.kind !== 'file') continue
    const entry = item.webkitGetAsEntry?.()
    if (entry?.isDirectory) return true
    const file = item.getAsFile()
    if (file && wanted(file.name)) return true
  }
  return false
}
